import { PluginConfigType } from 'Core/PluginCore/PluginsManager'
import { Studio } from 'Core/Studio'
import { appConfig } from 'Config/app-config'
import { BasePlugin } from 'Core/PluginCore/BasePlugin'
import { PluginConnector } from 'Core/PluginCore/PluginConnector'
import { ViolationSource } from 'shared/violations/types'
import {
    BlockEventSubscriptionType,
    ITaskBlock,
} from 'shared/express-interface/blocks-core/ITaskBlock'

export type GrammarPluginConfigType = {
    web_spell_checker_config: {
        maxErrorsAllowed: number
        proofreadDialog: boolean
        settings: boolean
        lang?: string
        customDictionaryId?: string
    }
}

export type WscInstanceType = {
    getContainerNode: () => HTMLElement
    subscribe: (event: string, cb: () => void) => void
    getProblemsCount: () => number
}

export type WindowWithWSCType = Window &
    typeof globalThis & {
        WEBSPELLCHECKER: {
            init: (arg0: {
                [key: string]:
                    | undefined
                    | boolean
                    | string
                    | number
                    | HTMLIFrameElement
                    | string[]
                    | null
                    | (() => void)
            }) => WscInstanceType
        }
    }

export class GrammarPlugin extends BasePlugin<PluginConfigType<GrammarPluginConfigType>> {
    public readonly id = 'Grammar Check'
    constructor(
        config: PluginConfigType<GrammarPluginConfigType>,
        pluginConnectors: PluginConnector[]
    ) {
        super(config, pluginConnectors)
    }

    public initialize(studio: Studio) {
        this.studio = studio
        this.initControl()
    }

    private showError(id: string, problemsCount: number) {
        /*
        This is important because the Grammar plugin can show both warning and error violations based on
        the number of grammar problems found in the input.
        */
        this.studio?.inputManager.removeViolation?.(`${id}_grammar`, 'warning')
        this.studio?.inputManager.addViolation?.({
            type: 'error',
            id: `${id}_grammar`,
            message: `Grammar problems found: ${problemsCount}`,
            source: ViolationSource.Plugin,
        })
    }

    private showWarning(id: string, problemsCount: number) {
        /*
        This is important because the Grammar plugin can show both warning and error violations based on
        the number of grammar problems found in the input.
        */
        this.studio?.inputManager.removeViolation?.(`${id}_grammar`, 'error')
        this.studio?.inputManager.addViolation?.({
            type: 'warning',
            id: `${id}_grammar`,
            message: `Grammar problems found: ${problemsCount}`,
            source: ViolationSource.Plugin,
        })
    }

    private initControl() {
        for (const connector of this.pluginConnectors) {
            const connectedBlocks = connector.getConnectedBlocks()
            for (const block of connectedBlocks) {
                block.subscription.subscribe(BlockEventSubscriptionType.RefAdded, () => {
                    const blockRefContainer = block.getRefContainer()
                    if (!blockRefContainer) {
                        console.error('Grammar Plugin: Ref container is not yet initialised')
                        return
                    }
                    this.initPluginInstance(block)
                })
            }
        }
    }

    private initPluginInstance(block: ITaskBlock) {
        const blockRefContainer = block.getRefContainer()

        const options = ['addWord', 'ignoreAll', 'toggle']
        if (this.config.config.web_spell_checker_config.proofreadDialog) {
            options.push('proofreadDialog')
        }
        if (this.config.config.web_spell_checker_config.settings) {
            options.push('settings')
        }

        const instance = (globalThis as WindowWithWSCType).WEBSPELLCHECKER.init({
            container: blockRefContainer as HTMLIFrameElement,
            lang: this.config.config.web_spell_checker_config.lang ?? 'en_US',
            actionItems: options,
            autoSearch: true,
            autoDestroy: true,
            autocorrect: false,
            autocomplete: true,
            withCredentials: true,
            enforceAI: true,
            serviceId: 'CustomerId',
            serviceHost: appConfig.expertsApiBaseUrl.replace(/https?:\/\//i, '').replace(/\/$/, ''), //Get URL without 'http' or 'https' string and remove trailing slash
            enableBadgeButton: false,
            customDictionaryIds:
                this.config.config.web_spell_checker_config.customDictionaryId ?? '',
            servicePath: 'v1/plugin-management/grammar-checker',
            // servicePort: '443',
            serviceProtocol: 'https',
            onAddWordToUserDictionary: () => {
                // Make the calls asynchronous as the instance doesn't reflect the updated problems count immediately
                Promise.resolve().then(() => this.getProblems(instance, block))
            },
        })

        if (instance) {
            this.getProblems(instance, block)

            instance.subscribe('problemCheckEnded', () => {
                this.getProblems(instance, block)
            })
        }
    }

    private getProblems(instance: WscInstanceType, block: ITaskBlock) {
        if (!instance) return
        const problemsCount = instance.getProblemsCount()
        if (problemsCount > 0) {
            const isLimitExceeded =
                problemsCount > this.config.config.web_spell_checker_config.maxErrorsAllowed
            isLimitExceeded
                ? this.showError(block.id, problemsCount)
                : this.showWarning(block.id, problemsCount)
        } else {
            this.studio?.inputManager.removeViolation?.(`${block.id}_grammar`)
        }
    }
}
